import { Component, OnInit } from '@angular/core';
import { LanguageService } from 'src/app/services/language.service';
import { BikeComponent } from '../../component/component';
import { HttpService } from 'src/app/services/http.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { UtilsService } from 'src/app/services/utils.service';
import * as CryptoJS from 'crypto-js';
import { DetailsViewMode } from '../../details-view-enum';
import { ConfirmationDialogData } from '../../modals/confirmation-dialog-data';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogData } from '../../modals/error-dialog-data';
import { ErrorDialogComponent } from '../../modals/error-dialog/error-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountService } from 'src/app/services/account.service';
import * as pako from 'pako';
import { DataParserService } from 'src/app/services/data-parser.service';

/**
 * Release details form for adding and viewing releases. 
 * Release fields: Release Version, Release Name, Locked, Mandatory release, Release notes, Release Components, Release check
 */

@Component({
  selector: 'app-release-details',
  templateUrl: './release-details.component.html',
  styleUrls: ['./release-details.component.scss'],
  standalone: false
})
export class ReleaseDetailsComponent implements OnInit {

  /** Defining view or add release */
  public mode: DetailsViewMode = DetailsViewMode.New;
  public loading: boolean = false;
  private skipConfirmation: boolean = false;

  public invalidFile: boolean = false;
  public fileTooLarge: boolean = false;
  public hexFileBlob: any = {};
  public hexFileSHA: string = "";
  public hexFileName: string = "";
  public hexFileSize: number = 0;
  public hexFileLink: string = "";
  public hexFileTouched: boolean = false;
  public allComponentsList: BikeComponent[] = [];
  public releaseId: string = "";
  public uploadDate: Date | null = null;
  public uploadUser: string | null = null;
  public activationStatus: number = -1;
  public approvalStatus: number = -1;
  public rolloutStatus: number = -1;
  public backendError: string = "";
  public editReleaseNotes: boolean = false;
  public hasDeliveredBikes: boolean = false;
  public noDataError: boolean = false;
  public releaseDeleteInactive: boolean = false;

  public oeOptionsList: { id: number, name: string }[] = [];
  public oeOptionsListAll: { id: number, name: string }[] = [];
  public componentClassOptionsList: { id: number, name: string }[] = [];
  public componentList: BikeComponent[] = [];
  public logbook: { UserId: string, Action: string, Details: string, Date: Date }[] = [];

  public softwareModifyRole: boolean = false;

  /** Release FormGroup definition */
  public releaseGroup: FormGroup = this.fb.group({
    version: ['', [
      Validators.required,
      Validators.pattern(/^\d+\.\d+\.\d+$/),
    ]],
    releaseNote: [''],
    oem: ['', [
      Validators.required
    ]],
    componentClass: [1, [
      Validators.required
    ]],
    component: ['', [
      Validators.required
    ]],
    componentVariant: ['', [
      Validators.required
    ]]
  });
  private initialFormValues: any;

  get oem() {
    return this.releaseGroup.get("oem");
  }

  get componentClass() {
    return this.releaseGroup.get("componentClass");
  }

  get component() {
    return this.releaseGroup.get("component");
  }

  get componentVariant() {
    return this.releaseGroup.get("componentVariant");
  }

  get releaseNote() {
    return this.releaseGroup.get("releaseNote");
  }

  get version() {
    return this.releaseGroup.get("version");
  }

  constructor(public languageService: LanguageService, private httpService: HttpService,
    private fb: FormBuilder, private router: Router, private route: ActivatedRoute,
    public utilsService: UtilsService, private matDialog: MatDialog, private snackBar: MatSnackBar,
    private accountService: AccountService, private dataParserService: DataParserService) { }

  /** Initializing components, loading viewable release details */
  ngOnInit(): void {
    this.initialFormValues = this.releaseGroup.value;
    this.loading = true;
    if (document.URL.includes("view-software")) {
      this.mode = DetailsViewMode.View;
    }
    if (document.URL.includes("edit-software")) {
      this.mode = DetailsViewMode.Edit;
    }

    forkJoin([
      document.URL.includes("edit-software") || document.URL.includes("new-software") ? this.httpService.getOEOptionsList(0, 0) : this.httpService.getOEOptionsList(1, 0),
      this.httpService.getComponentClassOptionsList(),
      this.httpService.getComponentList(),
      this.httpService.getOEOptionsList(1, 1)
    ]).subscribe((resp) => {
      this.oeOptionsList = resp[0];
      this.componentClassOptionsList = resp[1];
      this.oeOptionsListAll = resp[3];
      if ((resp[2] as any).success) {
        this.allComponentsList = this.dataParserService.initComponents(resp[2], this.oeOptionsListAll, this.componentClassOptionsList);
      }

      if (document.URL.includes("new-software")) {
        this.loading = false;
      }
      else if (document.URL.includes("view-software") || document.URL.includes("edit-software")) {
        this.route.queryParams
          .subscribe(params => {
            if (params['id'] == undefined) {
              this.goBack();
              return;
            }
            this.loadRelease(params);
          });
      }
    });
  }

  private loadRelease(queryParams: any) {
    forkJoin([this.httpService.getRelease(queryParams['id']), this.httpService.getSoftwareMonitorList(queryParams['id'])])
      .subscribe((resp: any) => {
        if (resp[1]?.success) {
          this.hasDeliveredBikes = this.hasAnyDeliveredBikesWithSoftware(resp[1].data);
          if (this.hasDeliveredBikes && this.mode == DetailsViewMode.Edit) {
            this.router.navigate(['/view-software'], { queryParams: { id: queryParams['id'] } });
          }
        }

        this.loading = false;
        if (resp[0]?.success) {
          const release = resp[0]?.data;
          this.logbook = release.logs.map((el: any) => {
            return {
              Date: new Date(el.created_at),
              UserId: el.doubleslash_id,
              Action: this.languageService.languageJSON[`ERROR_${el.event}_Action`],
              Details: this.languageService.languageJSON[`ERROR_${el.event}_Details`]
            }
          });

          const uploadId = release.logs.findIndex((el: any) => { return el.event == "SW_LOG_001" });
          if (uploadId != -1) {
            this.uploadDate = new Date(release.logs[uploadId].created_at);
            this.uploadUser = release.logs[uploadId].doubleslash_id;
            this.logbook.splice(uploadId, 1);
          }

          this.oem?.setValue(release.component_variant.OEM_ID);
          this.softwareModifyRole = this.accountService.hasSoftwareModifyRole() && this.accountService.getOEMs().includes(this.oem?.value);
          this.releaseId = release.id;
          this.hexFileName = release.filename.replace(".gz", "");
          this.hexFileSize = release.filesize;
          this.hexFileSHA = release.sha;
          this.hexFileLink = release.path;
          this.activationStatus = release.activation_status ? release.activation_status : 1;
          this.approvalStatus = release.approval_status ? release.approval_status : 1;
          this.rolloutStatus = release.rollout_status ? release.rollout_status : 1;
          this.releaseDeleteInactive = release.downloads > 0;

          this.releaseGroup.setValue({
            version: release.release_version,
            releaseNote: release.release_notes,
            oem: release.component_variant.OEM_ID,
            componentClass: release.component_variant.component.component_class,
            component: this.allComponentsList.find((el) => { return el.Id == release.component_variant.component.id }),
            componentVariant: release.component_variant.id
          });

          this.updateComponentList();

          this.initialFormValues = this.releaseGroup.value;
        }
        else {
          this.noDataError = true;
        }
      }, (error) => {
        if (error.status == 403) {
          this.router.navigate(['/software-overview']);
        }
        this.noDataError = true;
        this.loading = false;
      });
  }

  private hasAnyDeliveredBikesWithSoftware(swmonitorData: any): boolean {
    const approvalRolloutsDelivered = swmonitorData.approval_rollouts.findIndex((el: any) => { return el.deliveries > 0 }) != -1;
    const rolloutsDelivered = swmonitorData.rollouts.findIndex((el: any) => { return el.deliveries > 0 }) != -1;

    return approvalRolloutsDelivered || rolloutsDelivered;
  }

  /** User uploading firmware file by browsing a file */
  public fwUpload(event: any) {
    this.validateHexFile(event.target.files[0]);
  }

  public downloadFirmware() {
    this.httpService.downloadFirmware(this.releaseId).subscribe({
      next: async (gzipBlob) => {
        try {
          const extractedHex = await this.extractGzip(gzipBlob);

          // Create a blob for the extracted HEX file
          const hexBlob = new Blob([extractedHex], { type: 'text/plain' });

          // Create a download link
          const link = document.createElement('a');
          const url = window.URL.createObjectURL(hexBlob);

          link.href = url;
          link.download = this.hexFileName; // Replace with your desired file name
          link.click();

          // Cleanup
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error extracting the GZIP file:', error);
        }
      },
      error: (err) => {
        console.error('File download failed:', err);
      }
    });
  }

  // Method to extract a HEX file from a GZIP file
  extractGzip(blob: Blob) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          // Decompress GZIP using pako
          const decompressed = pako.ungzip(new Uint8Array(reader.result as ArrayBuffer), { to: 'string' });
          resolve(decompressed);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(blob);
    });
  }

  /** User uploading firmware file by drag and dropping */
  public onFileDropped(event: any) {
    this.validateHexFile(event[0]);
  }

  private validateHexFile(hexfile: File) {
    this.invalidFile = false;
    this.fileTooLarge = false;

    if (!hexfile || !this.isHexNamingValid(hexfile.name)) {
      this.invalidFile = true;
    }
    else if (hexfile.size / 1024 / 1024 > 10) {
      this.fileTooLarge = true;
    }
    else {
      this.backendError = "";
      this.processHexFile(hexfile);
    }
  }

  private processHexFile(hexfile: File) {
    this.hexFileTouched = true;
    this.hexFileName = hexfile.name;
    const reader = new FileReader();

    reader.onload = (event: any) => {
      this.hexFileBlob = new Blob([event.target.result], { type: "application/octet-stream" });
      const wordArray = CryptoJS.lib.WordArray.create(event.target.result);
      this.hexFileSHA = CryptoJS.SHA256(wordArray).toString();
    }

    reader.readAsArrayBuffer(hexfile);
  }

  private isHexNamingValid(name: string): boolean {
    const regex = /([a-zA-Z0-9_]+)-([a-zA-Z0-9_]+)-([a-zA-Z0-9_]+)-v([a-zA-Z0-9_.]+)\.hex/;
    return regex.test(name);
  }

  public clearHexFile(): void {
    this.hexFileName = "";
    this.hexFileBlob = {};
  }

  /** Sending new release to the Api endpoint. */
  public onSubmit(): void {
    if (this.mode == DetailsViewMode.Edit && !this.isFormChanged()) {
      this.snackBar.open(this.languageService.languageJSON.Global_NoChangesMade, undefined, { panelClass: 'success-snackbar', duration: 5000 });
      this.goBack();

      return;
    }

    this.backendError = "";
    this.releaseGroup.markAllAsTouched();
    this.hexFileTouched = true;

    if (this.mode == DetailsViewMode.Edit && this.releaseGroup.valid) {
      this.saveReleaseChanges();
    }

    // add new release
    if (this.mode == DetailsViewMode.New && this.releaseGroup.valid && this.hexFileName) {
      this.loading = true;
      this.httpService.addRelease(this.releaseGroup.value.releaseNote, this.componentVariant?.value, this.version?.value,
        this.hexFileSHA, this.hexFileBlob, this.hexFileName).subscribe((resp: any) => {
          this.loading = false;
          if (resp?.success) {
            this.skipConfirmation = true;
            this.snackBar.open(this.languageService.languageJSON.ReleaseDetails_SuccessMessage, undefined, { panelClass: 'success-snackbar', duration: 5000 });
            this.router.navigate(['/software-overview']);
          }
          else {
            this.backendError = this.languageService.languageJSON[`ReleaseDetails_ErrorCode_${resp?.code}`];
          }
        }, (error) => {
          this.loading = false;
        });
    }
  }

  public saveReleaseChanges() {
    this.httpService.editRelease(this.releaseId, this.releaseGroup.value.releaseNote, this.componentVariant?.value, this.version?.value)
      .subscribe((resp: any) => {
        if (resp?.success) {
          this.skipConfirmation = true;
          this.snackBar.open(this.languageService.languageJSON.ReleaseDetails_EditSuccessMessage, undefined, { panelClass: 'success-snackbar', duration: 5000 });
          this.router.navigate(['/software-overview']);
        }
        else {
          const data: ErrorDialogData = {
            title: this.languageService.languageJSON.Global_ServerError_Title,
            message: this.languageService.languageJSON.ReleaseDetails_CannotEdit_Error
          }
          this.matDialog.open(ErrorDialogComponent, { data: { data } });
        }
      });
  }

  public updateComponentList() {
    this.componentList = this.filterVariantForOEM(this.allComponentsList.filter((el) => { return el.ComponentClass == this.componentClass?.value }));
  }

  filterVariantForOEM(bikeComponents: BikeComponent[]): BikeComponent[] {
    return bikeComponents.map((bikeComponent) => {
      bikeComponent.Variants = bikeComponent.Variants.filter((el) => { return el.OEMId == this.oem?.value || el.Shared });
      return bikeComponent;
    });
  }

  public goBack() {
    this.skipConfirmation = true;
    this.router.navigate(['/software-overview']);
  }

  public editRelease() {
    this.router.navigate(['/edit-software'], { queryParams: { id: this.releaseId } });
  }

  /** Calling delete release Api endpoint */
  deleteRelease() {
    if (this.releaseDeleteInactive) {
      return;
    }
    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.ReleaseOverview_DeleteConfirmation_Title,
      message: this.languageService.languageJSON.Global_Delete_MessageWithName.replace("$name", `<b>${this.hexFileName}</b>`),
      buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.httpService.deleteRelease(+this.releaseId).subscribe((resp: any) => {
          if (resp?.success) {
            this.snackBar.open(this.languageService.languageJSON.ReleaseDetails_DeleteMessage.replace("$name", `${this.hexFileName}`), undefined, { panelClass: 'success-snackbar', duration: 5000 });
            this.goBack();
          }
          else {
            let errorMsg = this.languageService.languageJSON.ReleaseDetails_DeleteFailedMessage.replace("$name", `${this.hexFileName}`);

            if (resp.code == "E013") {
              errorMsg += `. ${this.languageService.languageJSON.ReleaseDetails_DeleteFailedMessage_E013}`;
            }
            this.snackBar.open(errorMsg, undefined, { panelClass: 'success-snackbar', duration: 5000 });
            
            if (resp.code == "E013") {
              this.goBack();
            }
          }
        });
      }
    });
  }

  public isFormChanged(): boolean {
    const currentValues = this.releaseGroup.value;
    return JSON.stringify(currentValues) != JSON.stringify(this.initialFormValues);
  }
}
